import React, { useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { Route } from 'react-router-dom'

import styles from './ActionFlow.module.scss'
import { useActionFlow, normalizeActionFlowPath } from './ActionFlowContext'
import type {
  ActionFlowFooter,
  ActionFlowHeader,
  ActionFlowLayout,
  ActionFlowChromeProps,
} from './types'

const renderChrome = (
  chrome: ActionFlowHeader | ActionFlowFooter | undefined,
  props: ActionFlowChromeProps
) => {
  if (chrome === undefined || chrome === null) return null

  if (React.isValidElement(chrome)) return chrome

  if (typeof chrome === 'function') return React.createElement(chrome, props)

  return chrome
}

export type ActionFlowPageProps = {
  path: string
  children: React.ReactNode
  header?: ActionFlowHeader
  footer?: ActionFlowFooter
  closeAfterMs?: number
  className?: string
  layout?: ActionFlowLayout
}

export const ActionFlowPage = ({
  header,
  footer,
  path,
  children,
  closeAfterMs,
  className,
  layout,
}: ActionFlowPageProps) => {
  const {
    close,
    navigate,
    isActionInProgress,
    isPending,
    allowOverflow,
    modalLayout,
    activeLayout,
    setActiveLayout,
  } = useActionFlow()

  useEffect(() => {
    setActiveLayout(layout)
    return () => setActiveLayout(undefined)
  }, [layout, setActiveLayout])

  useEffect(() => {
    if (!closeAfterMs) return
    const timer = setTimeout(() => {
      close()
    }, closeAfterMs)
    return () => clearTimeout(timer)
  }, [close, closeAfterMs])

  const chromeProps = useMemo<ActionFlowChromeProps>(
    () => ({
      close,
      navigate,
      isActionInProgress,
      isPending,
    }),
    [close, isActionInProgress, isPending, navigate]
  )

  const effectiveLayout = layout ?? activeLayout ?? modalLayout
  const isInlineRight = effectiveLayout === 'inlineRight'

  return (
    <Route path={normalizeActionFlowPath(path)}>
      <div className={clsx(styles.content, allowOverflow && styles.allowOverflow, className)}>
        {header ? (
          <div className={styles.header}>
            {typeof header === 'string' ? <h3>{header}</h3> : renderChrome(header, chromeProps)}
          </div>
        ) : null}
        <div className={clsx(styles.body, allowOverflow && styles.allowOverflow)}>{children}</div>
        {footer ? (
          <div
            className={clsx(
              styles.footer,
              isInlineRight ? styles.footerInlineRight : styles.footerFullWidth
            )}
          >
            {renderChrome(footer, chromeProps)}
          </div>
        ) : null}
      </div>
    </Route>
  )
}
